// ─── ID / timestamp helpers ───────────────────────────────
export const uid  = () => Math.random().toString(36).slice(2, 9).toUpperCase();
export const pad  = (n, len = 5) => String(n).padStart(len, '0');
export const rand = (min, max)  => Math.floor(Math.random() * (max - min + 1)) + min;
export const pick = (arr)       => arr[rand(0, arr.length - 1)];

export const nowTs = () => {
  const d = new Date();
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

export const pastTs = (daysAgo = rand(1, 180)) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(rand(8, 18), rand(0, 59), rand(0, 59));
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

// ─── Format helpers ───────────────────────────────────────
export const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-MY', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

export const formatDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-MY', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const formatCurrency = (value, currency = 'MYR') =>
  new Intl.NumberFormat('en-MY', { style: 'currency', currency }).format(value || 0);

// ─── Export helpers ───────────────────────────────────────
export const exportCSV = (data, filename, cols) => {
  const header = cols.map(c => c.label).join(',');
  const rows   = data.map(row =>
    cols.map(c => {
      const val = row[c.key];
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : (val ?? '');
    }).join(',')
  );
  const csv  = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Supabase column name mapper ──────────────────────────
// Converts snake_case DB columns → camelCase app fields
export const mapAsset = (row) => ({
  id:                   row.id,
  name:                 row.name,
  type:                 row.type,
  epc:                  row.epc,
  inventoryNumber:      row.inventory_number,
  status:               row.status,
  condition:            row.condition,
  currentFacility:      row.current_facility,
  currentFacilityName:  row.current_facility_name,
  lastSeen:             row.last_seen,
  checkedOutTo:         row.checked_out_to,
  value:                row.value,
  vendor:               row.vendor,
  brand:                row.brand,
  model:                row.model,
  serial_no:            row.serial_no,
  purchaseDate:         row.purchase_date,
  warrantyExpiry:       row.warranty_expiry,
  notes:                row.notes,
  org_id:               row.org_id,
  createdAt:            row.created_at,
  // Maintenance fields — kept snake_case to match the Maintenance tab form bindings
  warranty_expiry:        row.warranty_expiry,
  maintenance_date:       row.maintenance_date,
  next_maintenance_date:  row.next_maintenance_date,
  maintenance_interval:   row.maintenance_interval,
  maintenance_notes:      row.maintenance_notes,
  maintenance_status:     row.maintenance_status,
});

export const mapFacility = (row) => ({
  id:       row.id,
  code:     row.code,
  name:     row.name,
  type:     row.type,
  location: row.location,
  status:   row.status,
  manager:  row.manager,
  contact:  row.contact,
  antennas: row.antennas,
  org_id:   row.org_id,
});

export const mapDelivery = (row) => ({
  id:                 row.id,
  linkedRequestId:    row.linked_request_id,
  assetIds:           row.asset_ids || [],
  assetNames:         row.asset_names,
  fromFacilityId:     row.from_facility_id,
  fromFacilityName:   row.from_facility_name,
  toFacilityId:       row.to_facility_id,
  toFacilityName:     row.to_facility_name,
  carrier:            row.carrier,
  trackingNo:         row.tracking_no,
  status:             row.status,
  priority:           row.priority,
  createdBy:          row.created_by,
  createdAt:          row.created_at,
  dispatchedAt:       row.dispatched_at,
  deliveredAt:        row.delivered_at,
  estimatedDelivery:  row.estimated_delivery,
  notes:              row.notes,
  items:              row.items,
});

export const mapRequest = (row) => ({
  id:               row.id,
  assetId:          row.asset_id,
  assetName:        row.asset_name,
  epc:              row.epc,
  inventoryNumber:  row.inventory_number,
  fromFacilityId:   row.from_facility_id,
  fromFacilityName: row.from_facility_name,
  toFacilityId:     row.to_facility_id,
  toFacilityName:   row.to_facility_name,
  requestedBy:      row.requested_by,
  reason:           row.reason,
  status:           row.status,
  priority:         row.priority,
  approvedBy:       row.approved_by,
  deliveryId:       row.delivery_id,
  notes:            row.notes,
  requestedAt:      row.requested_at,
  updatedAt:        row.updated_at,
});
